import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { api, ApiError } from '../lib/api.js';
import { ENV_META } from '../lib/environments.js';
import type { EnvName } from '../lib/environments.js';
import { EnvBadge } from '../components/EnvironmentSwitcher.js';
import { ErrorState, Field, Icon } from '../components/ui.js';

export function Login({ onDone }: { onDone: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState<string[] | null>(null);
  const [code, setCode] = useState('');

  const passkeyLogin = async (second: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const path = second ? '/api/auth/passkey' : '/api/auth/passkey/login';
      const o = await api.post<{
        options: Parameters<typeof startAuthentication>[0]['optionsJSON'];
        challengeId: string;
      }>(`${path}/options`);
      const response = await startAuthentication({ optionsJSON: o.options });
      await api.post(`${path}/${second ? 'verify' : 'verify'}`, {
        challengeId: o.challengeId,
        response,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Přihlášení passkey se nepovedlo.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/mfa', { code });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kód se nepodařilo ověřit.');
    } finally {
      setBusy(false);
    }
  };

  /* /health je veřejné: ještě před přihlášením je vidět, do kterého prostředí se hlásíte. */
  const health = useQuery<{ environment?: string | null }>({
    queryKey: ['health'],
    queryFn: async () => (await fetch('/health', { cache: 'no-store' })).json(),
    retry: false,
  });
  const env = health.data?.environment;
  const envName: EnvName | null = env && env in ENV_META ? (env as EnvName) : null;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ mfaRequired?: boolean; methods?: string[] }>('/api/auth/login', {
        email,
        password,
      });
      if (r.mfaRequired) {
        setMfa(r.methods ?? ['totp']);
        setPassword('');
      } else onDone();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'E-mail nebo heslo nesouhlasí. Zkontrolujte překlepy a zkuste to znovu.'
          : err instanceof Error
            ? err.message
            : 'Přihlášení se nepovedlo.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <div className="logincard">
        <div className="brandline">
          <span className="logo" aria-hidden="true">
            <Icon name="route" size={22} />
          </span>
          <div>
            <h1>Marketing Hub</h1>
            <p className="lede">Administrace ponákupní komunikace.</p>
          </div>
        </div>
        {envName ? (
          <div
            className={`envbadge env-${envName}`}
            data-testid="login-environment"
            title={ENV_META[envName].short}
          >
            <EnvBadge name={envName} showDescription />
          </div>
        ) : null}
        {mfa ? (
          <form className="panel" onSubmit={submitCode} data-testid="mfa-form">
            <Field label="Kód z aplikace nebo záložní kód">
              <input
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            {error ? <ErrorState error={new Error(error)} title="Přihlášení se nepovedlo" /> : null}
            <button className="btn pri" type="submit" disabled={busy || code.trim().length < 6}>
              Ověřit a přihlásit
            </button>
            {mfa.includes('passkey') ? (
              <button
                className="btn"
                type="button"
                disabled={busy}
                onClick={() => void passkeyLogin(true)}
              >
                Použít passkey
              </button>
            ) : null}
          </form>
        ) : (
          <form className="panel" onSubmit={submit}>
            <Field label="E-mail nebo jméno">
              <input
                type="text"
                value={email}
                autoComplete="username"
                autoFocus
                required
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Heslo">
              <input
                type="password"
                value={password}
                autoComplete="current-password"
                required
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? <ErrorState error={new Error(error)} title="Přihlášení se nepovedlo" /> : null}
            <button className="btn pri" type="submit" disabled={busy}>
              {busy ? 'Přihlašuji…' : 'Přihlásit se'}
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() => void passkeyLogin(false)}
            >
              Přihlásit se passkey
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
